import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { normalizeApiError } from '../api/apiError';
import { getFaceEnrollmentConfig } from '../config/faceEnrollmentConfig';
import type {
  AddFaceSampleResult,
  DeleteFaceRegistrationResult,
  EmployeeAzureRegistration,
  EmployeeFaceSample,
  EnrollmentCaptureSource,
  EnsureAzurePersonResult,
  FaceDeletionStatus,
  FaceRegistrationStatus,
  PersonGroupTrainingStatus,
  PersonGroupTrainingStatusValue,
} from '../types/faceRegistration';
import { faceRegistrationErrorMessage } from '../utils/faceRegistrationErrors';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function boolOrNull(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return null;
}

function isUnregisteredError(error: unknown) {
  const apiError = normalizeApiError(error);
  return (
    apiError.code === 'AZURE_PERSON_NOT_CONFIGURED' ||
    apiError.code === 'FACE_REGISTRATION_NOT_FOUND' ||
    apiError.statusCode === 404
  );
}

function isSoftLoadFailure(error: unknown) {
  const apiError = normalizeApiError(error);
  return (
    isUnregisteredError(error) ||
    apiError.code === 'AZURE_FACE_DISABLED' ||
    apiError.code === 'AZURE_FACE_UNAVAILABLE' ||
    apiError.code === 'AZURE_FACE_CONFIGURATION_ERROR' ||
    apiError.code === 'AZURE_FACE_INVALID_REQUEST' ||
    apiError.code === 'AZURE_PERSON_MAPPING_MISSING'
  );
}

function deletionPendingFrom(personStatus: string | null, syncStatus: string | null) {
  return (
    personStatus === 'DELETE_PENDING' ||
    personStatus === 'DELETING' ||
    syncStatus === 'DELETE_PENDING' ||
    syncStatus === 'DELETING'
  );
}

function trainingSucceeded(trainingStatus: string | null) {
  return (
    trainingStatus === 'SUCCEEDED' ||
    trainingStatus === 'NOT_REQUIRED' ||
    trainingStatus == null
  );
}

function computeIsReady(input: {
  azurePersonConfigured: boolean;
  personStatus: string | null;
  syncStatus: string | null;
  activeFaceCount: number;
  trainingStatus: string | null;
  deletionPending: boolean;
  providerTrainingRequired?: boolean | null;
}) {
  const trainingOk =
    input.providerTrainingRequired === false
      ? true
      : trainingSucceeded(input.trainingStatus);
  return (
    input.azurePersonConfigured &&
    !input.deletionPending &&
    input.personStatus === 'ACTIVE' &&
    input.syncStatus === 'SYNCED' &&
    input.activeFaceCount >= 1 &&
    trainingOk
  );
}

function emptyRegistration(employeeId: number): EmployeeAzureRegistration {
  return {
    employeeId,
    faceRegistrationId: null,
    azurePersonConfigured: false,
    biometricProvider: null,
    providerTrainingRequired: null,
    personStatus: null,
    syncStatus: null,
    registrationStatus: null,
    trainingStatus: null,
    activeFaceCount: 0,
    maxFaceSamples: null,
    isReady: false,
    deletionPending: false,
    retryAllowed: false,
  };
}

function normalizeSample(row: ObjectValue): EmployeeFaceSample {
  return {
    sampleNumber: numberOrNull(row.faceSampleNumber ?? row.captureSequence ?? row.sampleNumber),
    captureSource: textOrNull(row.captureSource),
    capturedAt: textOrNull(row.capturedAt ?? row.createdAt ?? row.registeredAt),
    isPrimary: boolOrNull(row.isPrimary) === true,
    isActive: boolOrNull(row.isActive),
    syncStatus: textOrNull(row.syncStatus ?? row.azureSyncStatus),
  };
}

function normalizeRegistration(
  employeeId: number,
  registration: ObjectValue | null,
  azurePerson: ObjectValue | null,
  trainingStatus: string | null,
  activeFaceCountOverride?: number,
): EmployeeAzureRegistration {
  if (!registration) return emptyRegistration(employeeId);

  const personStatus = textOrNull(registration.personStatus ?? registration.azurePersonStatus);
  const syncStatus = textOrNull(registration.syncStatus ?? registration.azureSyncStatus);
  const deletionPending = deletionPendingFrom(personStatus, syncStatus);
  const activeFaceCount =
    activeFaceCountOverride ??
    numberOrNull(azurePerson?.persistedFaceCount) ??
    numberOrNull(registration.activeFaceCount) ??
    numberOrNull(registration.totalSamples) ??
    0;
  const providerTrainingRequired = boolOrNull(
    registration.providerTrainingRequired ?? registration.trainingRequired,
  );
  const biometricProvider = textOrNull(
    registration.biometricProvider ?? registration.providerCode,
  );
  const resolvedProviderTrainingRequired =
    providerTrainingRequired ??
    (biometricProvider === 'AWS_REKOGNITION'
      ? false
      : biometricProvider === 'AZURE_FACE'
        ? true
        : null);
  const resolvedTrainingStatus =
    trainingStatus ||
    textOrNull(registration.trainingStatus ?? registration.providerTrainingStatus) ||
    (resolvedProviderTrainingRequired === false ? 'NOT_REQUIRED' : null);

  return {
    employeeId,
    faceRegistrationId: numberOrNull(
      registration.faceRegistrationId ?? registration.registrationId,
    ),
    azurePersonConfigured: true,
    biometricProvider,
    providerTrainingRequired: resolvedProviderTrainingRequired,
    personStatus,
    syncStatus,
    registrationStatus: textOrNull(registration.registrationStatus),
    trainingStatus: resolvedTrainingStatus,
    activeFaceCount,
    maxFaceSamples: numberOrNull(registration.maxFaceSamples),
    isReady: computeIsReady({
      azurePersonConfigured: true,
      personStatus,
      syncStatus,
      activeFaceCount,
      trainingStatus: resolvedTrainingStatus,
      deletionPending,
      providerTrainingRequired: resolvedProviderTrainingRequired,
    }),
    deletionPending,
    retryAllowed: false,
  };
}

async function loadTrainingStatus(): Promise<PersonGroupTrainingStatusValue | null> {
  try {
    const response = await apiClient.get<Envelope<ObjectValue>>(
      API_ENDPOINTS.FACE.TRAINING_STATUS,
    );
    return textOrNull(response.data.data?.trainingStatus);
  } catch (error) {
    const apiError = normalizeApiError(error);
    if (apiError.code === 'AZURE_TRAINING_FAILED') return 'FAILED';
    if (
      apiError.code === 'AZURE_PERSON_GROUP_NOT_CONFIGURED' ||
      apiError.code === 'AZURE_PERSON_GROUP_NOT_READY' ||
      apiError.code === 'AZURE_FACE_DISABLED' ||
      apiError.code === 'AZURE_FACE_UNAVAILABLE' ||
      apiError.code === 'AZURE_FACE_CONFIGURATION_ERROR' ||
      apiError.code === 'AZURE_FACE_AUTHENTICATION_FAILED' ||
      apiError.code === 'BIOMETRIC_PROVIDER_UNAVAILABLE' ||
      apiError.code === 'BIOMETRIC_RESOURCE_NOT_FOUND' ||
      apiError.statusCode === 404 ||
      apiError.statusCode === 503
    ) {
      return null;
    }
    throw error;
  }
}

async function loadSafeSamples(employeeId: number): Promise<EmployeeFaceSample[]> {
  try {
    const response = await apiClient.get<
      Envelope<{ samples?: ObjectValue[]; registered?: boolean }>
    >(API_ENDPOINTS.EMPLOYEES.FACE_REGISTRATION(employeeId));
    const samples = response.data.data?.samples;
    return Array.isArray(samples) ? samples.map(normalizeSample) : [];
  } catch {
    return [];
  }
}

function registrationSkipsPersonGroupTraining(registration: ObjectValue | null) {
  if (!registration) return false;
  const biometricProvider = textOrNull(
    registration.biometricProvider ?? registration.providerCode,
  );
  const providerTrainingRequired = boolOrNull(
    registration.providerTrainingRequired ?? registration.trainingRequired,
  );
  if (providerTrainingRequired === false) return true;
  if (biometricProvider === 'AWS_REKOGNITION') return true;
  const providerTrainingStatus = textOrNull(
    registration.providerTrainingStatus ?? registration.trainingStatus,
  );
  return providerTrainingStatus === 'NOT_REQUIRED';
}

export async function getFaceRegistration(
  employeeId: number,
): Promise<FaceRegistrationStatus> {
  try {
    const response = await apiClient.get<
      Envelope<{ registration?: ObjectValue; azurePerson?: ObjectValue }>
    >(API_ENDPOINTS.EMPLOYEES.AZURE_PERSON(employeeId));
    const data = response.data.data ?? {};
    const registrationRow = (data.registration as ObjectValue) ?? null;
    const faces = await loadSafeSamples(employeeId);
    const activeFaceCount =
      numberOrNull(data.azurePerson?.persistedFaceCount) ??
      faces.filter(sample => sample.isActive !== false).length;

    let trainingStatus: PersonGroupTrainingStatusValue | null = null;
    if (!registrationSkipsPersonGroupTraining(registrationRow)) {
      try {
        trainingStatus = await loadTrainingStatus();
      } catch {
        trainingStatus = null;
      }
    }

    const registration = normalizeRegistration(
      employeeId,
      registrationRow,
      (data.azurePerson as ObjectValue) ?? null,
      trainingStatus,
      activeFaceCount,
    );
    return {
      registration,
      faces,
      trainingStatus: trainingStatus ?? registration.trainingStatus,
    };
  } catch (error) {
    if (isSoftLoadFailure(error)) {
      return {
        registration: emptyRegistration(employeeId),
        faces: [],
        trainingStatus: null,
      };
    }
    throw error;
  }
}

export async function ensureAzurePerson(
  employeeId: number,
): Promise<EnsureAzurePersonResult> {
  const response = await apiClient.post<
    Envelope<{
      createdInAzure?: boolean;
      createdLocally?: boolean;
      registration?: ObjectValue;
    }>
  >(API_ENDPOINTS.EMPLOYEES.ENSURE_AZURE_PERSON(employeeId), {});

  const data = response.data.data ?? {};
  const registration = normalizeRegistration(
    employeeId,
    (data.registration as ObjectValue) ?? null,
    null,
    null,
  );

  return {
    createdInAzure: data.createdInAzure === true,
    createdLocally: data.createdLocally === true,
    registration,
  };
}

export async function addFaceSample(
  employeeId: number,
  input: {
    imageUri: string;
    mimeType?: string;
    fileName?: string;
    captureSource?: EnrollmentCaptureSource;
    remarks?: string;
  },
): Promise<AddFaceSampleResult> {
  const config = getFaceEnrollmentConfig();
  const formData = new FormData();
  formData.append('image', {
    uri: input.imageUri,
    type: input.mimeType || 'image/jpeg',
    name: input.fileName || 'face.jpg',
  } as unknown as Blob);
  formData.append('captureSource', input.captureSource || config.captureSource);
  if (input.remarks?.trim()) {
    formData.append('remarks', input.remarks.trim());
  }

  const response = await apiClient.post<
    Envelope<{ face?: ObjectValue; trainingRequired?: boolean }>
  >(config.addSamplePath(employeeId), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const data = response.data.data ?? {};
  return {
    face: normalizeSample((data.face as ObjectValue) ?? {}),
    trainingRequired: data.trainingRequired === true,
  };
}

export async function startPersonGroupTraining(): Promise<PersonGroupTrainingStatus> {
  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.FACE.TRAIN_PERSON_GROUP,
    {},
  );
  return {
    trainingStatus: textOrNull(response.data.data?.trainingStatus) ?? 'RUNNING',
  };
}

export async function getPersonGroupTrainingStatus(): Promise<PersonGroupTrainingStatus> {
  try {
    const response = await apiClient.get<Envelope<ObjectValue>>(
      API_ENDPOINTS.FACE.TRAINING_STATUS,
    );
    return {
      trainingStatus: textOrNull(response.data.data?.trainingStatus) ?? 'NOT_STARTED',
    };
  } catch (error) {
    const apiError = normalizeApiError(error);
    if (apiError.code === 'AZURE_TRAINING_FAILED') {
      return { trainingStatus: 'FAILED' };
    }
    throw error;
  }
}

function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export async function pollPersonGroupTrainingStatus(
  options: {
    intervalMs?: number;
    timeoutMs?: number;
    shouldContinue?: () => boolean;
  } = {},
): Promise<PersonGroupTrainingStatus> {
  const config = getFaceEnrollmentConfig();
  const intervalMs = Math.max(1000, options.intervalMs ?? config.pollIntervalMs);
  const timeoutMs = options.timeoutMs ?? config.resultTimeoutMs;
  const startedAt = Date.now();
  let last: PersonGroupTrainingStatus = { trainingStatus: 'RUNNING' };

  while (Date.now() - startedAt <= timeoutMs) {
    if (options.shouldContinue && !options.shouldContinue()) {
      return last;
    }
    last = await getPersonGroupTrainingStatus();
    if (last.trainingStatus === 'SUCCEEDED' || last.trainingStatus === 'FAILED') {
      return last;
    }
    if (Date.now() - startedAt + intervalMs > timeoutMs) break;
    await delay(intervalMs);
  }

  const error = new Error(faceRegistrationErrorMessage('AZURE_TRAINING_TIMEOUT'));
  (error as Error & { code?: string }).code = 'AZURE_TRAINING_TIMEOUT';
  throw error;
}

export async function deleteFaceRegistration(
  employeeId: number,
  reason: string,
): Promise<DeleteFaceRegistrationResult> {
  const payload = JSON.stringify({ reason: reason.trim() });
  const response = await apiClient.delete<Envelope<ObjectValue>>(
    API_ENDPOINTS.EMPLOYEES.DELETE_FACE_REGISTRATION(employeeId),
    {
      data: payload,
      headers: { 'Content-Type': 'application/json' },
      transformRequest: [
        data => (typeof data === 'string' ? data : JSON.stringify(data)),
      ],
    },
  );
  const data = response.data.data ?? {};
  return {
    employeeId: numberOrNull(data.employeeId) ?? employeeId,
    faceRegistrationId: numberOrNull(data.faceRegistrationId),
    azureDeleted: data.azureDeleted === true || data.alreadyDeleted === true,
    alreadyDeleted: data.alreadyDeleted === true,
    localRegistrationActive: boolOrNull(data.localRegistrationActive) ?? undefined,
    personStatus: textOrNull(data.personStatus),
    syncStatus: textOrNull(data.syncStatus),
  };
}

export async function getFaceDeletionStatus(
  employeeId: number,
): Promise<FaceDeletionStatus> {
  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.EMPLOYEES.FACE_DELETION_STATUS(employeeId),
  );
  const data = response.data.data ?? {};
  return {
    employeeId: numberOrNull(data.employeeId) ?? employeeId,
    faceRegistrationId: numberOrNull(data.faceRegistrationId),
    personStatus: textOrNull(data.personStatus),
    syncStatus: textOrNull(data.syncStatus),
    isActive: boolOrNull(data.isActive),
    deactivatedAt: textOrNull(data.deactivatedAt),
    retryAllowed: data.retryAllowed === true,
    errorCode: textOrNull(data.errorCode),
  };
}

export async function retryFaceDeletion(
  employeeId: number,
): Promise<DeleteFaceRegistrationResult> {
  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.EMPLOYEES.RETRY_FACE_DELETION(employeeId),
    {},
  );
  const data = response.data.data ?? {};
  return {
    employeeId: numberOrNull(data.employeeId) ?? employeeId,
    faceRegistrationId: numberOrNull(data.faceRegistrationId),
    azureDeleted: data.azureDeleted === true || data.alreadyDeleted === true,
    alreadyDeleted: data.alreadyDeleted === true,
    personStatus: textOrNull(data.personStatus),
    syncStatus: textOrNull(data.syncStatus),
  };
}
